import { c as _c } from "react/compiler-runtime";
import type { SVGProps } from "react";
import { AmbulanceIcon, ArrowRightLeft, Coins, Droplets, Images, Newspaper, Pickaxe, Store, Tractor, Wallet2Icon, ZapIcon, type LucideIcon } from "lucide-react";
export type PortfolioTabType = "portfolio" | "txs" | "nfts";

/**
 * Icon component type that supports both Lucide icons and custom SVG components.
 * Custom icons should accept className for sizing (e.g., "h-5 w-5").
 */
export type IconComponent = LucideIcon | React.ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Example custom SVG icon component.
 * Custom icons should accept className and spread remaining props to the SVG element.
 */
export const StarIcon = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path fill="url(#SVG0qolucHE)" d="M9.104 2.899a1 1 0 0 1 1.794 0l1.93 3.911l4.317.627a1 1 0 0 1 .554 1.706l-3.124 3.045l.738 4.299a1 1 0 0 1-1.451 1.054l-3.86-2.03l-3.862 2.03a1 1 0 0 1-1.45-1.054l.737-4.3l-3.124-3.044a1 1 0 0 1 .554-1.706l4.317-.627z" />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <g fill="none">{t1}<defs><linearGradient id="SVG0qolucHE" x1={18} x2={1.55} y1={18} y2={2.477} gradientUnits="userSpaceOnUse"><stop stopColor="#ff6f47" /><stop offset={1} stopColor="#ffcd0f" /></linearGradient></defs></g>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== className || $[6] !== props) {
    t3 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" className={className} aria-hidden="true" {...props}>{t2}</svg>;
    $[5] = className;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export const BridgeIcon = t0 => {
  const $ = _c(7);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path fill="currentColor" d="M232 156h-28v-44.51a76.4 76.4 0 0 0 23.49 15a12 12 0 0 0 9-22.25A51.81 51.81 0 0 1 204 56a12 12 0 0 0-24 0a52 52 0 0 1-104 0a12 12 0 0 0-24 0a51.81 51.81 0 0 1-32.5 48.22a12 12 0 1 0 9 22.25a76.4 76.4 0 0 0 23.49-15V156H24a12 12 0 0 0 0 24h28v20a12 12 0 0 0 24 0v-20h104v20a12 12 0 0 0 24 0v-20h28a12 12 0 0 0 0-24m-92-24.95V156h-24v-24.95a76.3 76.3 0 0 0 24 0m-64-19.67a76.4 76.4 0 0 0 16 11.53V156H76ZM164 156v-33.09a76.4 76.4 0 0 0 16-11.53V156Z" />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== className || $[5] !== props) {
    t2 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" className={className} aria-hidden="true" {...props}>{t1}</svg>;
    $[4] = className;
    $[5] = props;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export const ShibIcon = t0 => {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    const {
      className,
      ...t1
    } = t0;
    props = t1;
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <g fill="none"><path fill="#eb9a2e" d="M4.246 14.796c0-1.654.819-3.62 1.228-4.397c-.625-1.964-1.64-6.074-.703-6.8c.938-.726 3.2 1.687 4.214 2.984c.415-.219 1.589-.656 2.974-.656s2.686.437 3.165.656c2.727-3.052 3.868-3.26 4.097-2.984c.964 1.218-.04 5.04-.664 6.8c.403.644 1.205 2.426 1.205 4.398c0 2.463-3.54 5.995-7.818 5.995c-4.279 0-7.698-3.928-7.698-5.996" /><path fill="#e58014" fillRule="evenodd" d="M5.72 9.372c-.367-1.237-.988-3.881-.55-4.564c.44-.683 2.199 1.027 3.023 1.967c-.475.38-1.635 1.43-2.474 2.597m12.651 0c.366-1.237.988-3.881.549-4.564c-.44-.683-2.198 1.027-3.023 1.967c.475.38 1.636 1.43 2.474 2.597" clipRule="evenodd" /><path fill="#fefff7" d="M9.19 11.081q-.617.112-1.242.08a.314.314 0 0 1-.305-.285c-.04-.506.152-1.182.726-1.155c.689.032.99.542 1.095.961c.047.189-.085.365-.274.4m5.65 0c.49.09.942.097 1.243.08a.315.315 0 0 0 .305-.285c.039-.506-.153-1.182-.727-1.155c-.688.032-.989.542-1.094.961c-.047.189.084.365.273.4" /><path fill="#eaeae8" d="M11.526 20.69c-5.887-.897-7.305-4.31-7.279-5.904c3.042-.225 4.085.952 4.226 1.568c.081-.938.873-2.817 3.389-2.817s3.307 1.878 3.388 2.817a4.17 4.17 0 0 1 4.614-1.368c-1.44 4.787-6.159 5.798-8.338 5.704" /><path fill="#000001" fillRule="evenodd" d="M12.778 14.442h-1.633a.413.413 0 0 0-.41.416v.806c.024.26.265.76 1.023.844v.8a.5.5 0 0 0-.115.168h-.977v.01a1.8 1.8 0 0 0-.339-.247a.103.103 0 0 0-.147.12a.1.1 0 0 0 .05.063c.065.036.166.1.252.175a1 1 0 0 1 .108.112q.04.056.044.084q.006.032.026.054v.447c0 .057.046.104.102.104a.103.103 0 0 0 .102-.104v-.611h2.196v.61c0 .058.046.105.102.105a.1.1 0 0 0 .073-.031a.1.1 0 0 0 .03-.073v-.5l.003-.018l.002-.004a.2.2 0 0 1 .025-.035a1 1 0 0 1 .114-.105a3 3 0 0 1 .286-.198a.105.105 0 0 0-.026-.19a.1.1 0 0 0-.078.01a3 3 0 0 0-.31.215l-.008.006h-1.025a.4.4 0 0 0-.08-.105v-.858c.786-.07 1.009-.583 1.02-.848v-.806a.413.413 0 0 0-.41-.416m-1.501 3.54l.216.265l.166-.157l.3.29l.31-.29l.145.157l.2-.266z" clipRule="evenodd" /><path fill="#000" d="M7.507 11.766c-.302-.118-.305.16-.269.312v.232a1.04 1.04 0 0 0 .664.969c.913.343 2.456.818 2.287.32c-.223-.655-2.306-1.684-2.682-1.833m8.935 0c.3-.118.304.16.268.312v.232a1.04 1.04 0 0 1-.664.969c-.913.343-2.456.818-2.287.32c.223-.655 2.306-1.684 2.683-1.833" /><path fill="#000" fillRule="evenodd" d="M8.981 6.106C8.095 5.009 6.998 3.78 5.643 3.242c-3.164-1.257-.922 5.982-.59 7.026c-.435.842-1.256 2.814-1.256 4.48c0 3.606 5.209 6.252 8.208 6.252c3.132 0 8.198-2.56 8.198-6.252c0-1.955-.743-3.679-1.18-4.414c.673-1.916 1.915-5.405.573-7.126c-.904-1.082-4.11 2.395-4.5 2.823c-.585-.24-1.808-.526-3.138-.526c-1.306 0-2.441.357-2.977.6M4.764 3.624c-.075.057-.144.16-.199.324a2.5 2.5 0 0 0-.1.623c-.027.5.03 1.132.14 1.814c.215 1.36.63 2.88.945 3.864l.028.085l-.042.078c-.411.774-1.225 2.72-1.225 4.336c0 .468.197 1.068.582 1.716c.383.644.94 1.318 1.632 1.931c1.387 1.226 3.363 2.046 5.48 2.046c2.123 0 4.007-.725 5.422-1.9c1.424-1.183 2.272-2.641 2.272-3.793c0-1.938-.801-3.697-1.195-4.323l-.054-.086l.034-.096c.314-.879.724-2.273.934-3.595c.105-.661.159-1.297.128-1.838c-.032-.543-.146-.96-.348-1.216c-.006-.004-.046-.03-.174-.01c-.149.022-.377.104-.699.299c-.642.387-1.61 1.192-2.994 2.724l-.1.11l-.135-.06c-.466-.212-1.764-.644-3.138-.644c-1.054 0-2.144.225-3.082.717c-.675-.855-1.407-1.677-2.249-2.371c-.37-.306-1.33-1.144-1.863-.735" clipRule="evenodd" /></g>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props) {
    t2 = <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" {...props}>{t1}</svg>;
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const DCIcon = t0 => {
  const $ = _c(2);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = <img src="/images/chains/2000.webp" alt="Dogechain" className={className} />;
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export type NavActionType = {
  type: "portfolio";
  tab: PortfolioTabType;
} | {
  type: "external";
  url: string;
};
export interface NavSubItem {
  label: string;
  href?: string;
  icon: IconComponent;
  action?: NavActionType;
}
export interface NavCategory {
  id: string;
  label: string;
  description?: string | React.ReactElement;
  icon: IconComponent;
  subItems: NavSubItem[];
}
export interface DrawerCategory {
  id: string;
  label: string;
  description?: string | React.ReactElement;
  items: NavSubItem[];
}

// Main navigation categories with their sub-items
export const navCategories: NavCategory[] = [{
  id: "tokens",
  label: "Tokens",
  description: "Manage your tokens and liquidity on any DEX",
  icon: ArrowRightLeft,
  subItems: [{
    label: "Swap",
    href: "/swap",
    icon: ArrowRightLeft
  }, {
    label: "Liquidity",
    href: "/liquidity",
    icon: Droplets
  }, {
    label: "Send",
    href: "/send",
    icon: Coins
  }, {
    label: "Portfolio",
    icon: Wallet2Icon,
    action: {
      type: "portfolio",
      tab: "portfolio"
    }
  }]
}, {
  id: "bridge",
  label: "Bridge",
  icon: BridgeIcon,
  subItems: [{
    label: "CCIP Bridge",
    href: "/bridge",
    icon: BridgeIcon
  }, {
    label: "Hyperlane",
    icon: ZapIcon,
    action: {
      type: "external",
      url: "https://warp.chewyswap.dog"
    }
  }]
}, {
  id: "nfts",
  label: "NFTs",
  icon: Images,
  subItems: [{
    label: "Staking",
    href: "/nft-staking",
    icon: Pickaxe
  }, {
    label: "Marketplace",
    href: "/marketplace",
    icon: Store
  }, {
    label: "My NFTs",
    action: {
      type: "portfolio",
      tab: "nfts"
    },
    icon: Images
  }]
}, {
  id: "earn",
  label: "Earn",
  icon: Tractor,
  subItems: [{
    label: "Farms",
    href: "/farms",
    icon: Tractor
  }, {
    label: "Shibarium",
    href: "/farms/shibarium/",
    icon: ShibIcon
  }, {
    label: "Dogechain",
    href: "/farms/dogechain/",
    icon: DCIcon
  }]
}];

// Full drawer categories (includes all nav items plus additional "More" section)
export const drawerCategories: DrawerCategory[] = [{
  id: "tokens",
  label: "Tokens",
  description: <div className="flex flex-row gap-x-2"><div className="flex animate-pulse"><StarIcon /></div><div> assets on any DEX</div></div>,
  items: [{
    label: "Swap",
    href: "/swap",
    icon: ArrowRightLeft
  }, {
    label: "Liquidity",
    href: "/liquidity",
    icon: Droplets
  }, {
    label: "Send",
    href: "/send",
    icon: Coins
  }, {
    label: "Portfolio",
    icon: Wallet2Icon,
    action: {
      type: "portfolio",
      tab: "portfolio"
    }
  }]
}, {
  id: "bridge",
  label: "Bridge",
  description: "Cross-chain Transfers",
  items: [{
    label: "CCIP Bridge",
    href: "/bridge",
    icon: BridgeIcon
  }, {
    label: "Hyperlane",
    icon: ZapIcon,
    action: {
      type: "external",
      url: "https://warp.chewyswap.dog"
    }
  }]
}, {
  id: "nfts",
  label: "NFTs",
  description: "Chewy's JPEG Heaven!",
  items: [{
    label: "NFT Staking",
    href: "/nft-staking",
    icon: Pickaxe
  }, {
    label: "Marketplace",
    href: "/marketplace",
    icon: Store
  }]
}, {
  id: "earn",
  label: "Earn + More",
  description: "Farms, News, and Rescue Stuck Funds",
  items: [{
    label: "Farms",
    href: "/farms",
    icon: Tractor
  }, {
    label: "News",
    href: "/news",
    icon: Newspaper
  }, {
    label: "Rescue Mission",
    href: "/rescue",
    icon: AmbulanceIcon
  }]
}];

// Animation constants
export const springTransition = {
  type: "spring" as const,
  stiffness: 400,
  damping: 30
};
export const easeTransition = {
  duration: 0.2,
  ease: [0.4, 0, 0.2, 1] as const
};
export const staggerChildren = {
  staggerChildren: 0.03
};